// LeadershipRatingsDashboard.jsx - With Fiscal Year Filtering

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { Card, Modal, ModalTitle, ModalContent, ModalActions, Button, CircularLoader, NoticeBox } from '@dhis2/ui';
import BellCurveChart from './BellCurveChart';
import { DATA_ELEMENTS } from '../../appraisal/api/appraisalConstants';

// ─── Responsive hook ─────────────────────────────────────────
const useMediaQuery = (query) => {
    const [matches, setMatches] = React.useState(
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false
    );

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        const mql = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        setMatches(mql.matches);
        mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
        return () => {
            mql.removeEventListener ? mql.removeEventListener('change', handler) : mql.removeListener(handler);
        };
    }, [query]);

    return matches;
};

const RATING_SCALE_NAMESPACE = 'leadership-rating-scale';

// ============================================================
// HELPER FUNCTIONS - Defined OUTSIDE the component
// ============================================================

const getDefaultColor = (rating) => {
    const colors = {
        1: '#dc3545',
        2: '#ff9800',
        3: '#ffc107',
        4: '#4caf50',
        5: '#28a745'
    };
    return colors[rating] || '#6c757d';
};

const getDefaultScale = () => ({
    1: { label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations', bgColor: '#dc354520' },
    2: { label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations', bgColor: '#ff980020' },
    3: { label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently', bgColor: '#ffc10720' },
    4: { label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations', bgColor: '#4caf5020' },
    5: { label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations', bgColor: '#28a74520' }
});

const getDataStoreKey = (orgId) => orgId ? `scale_${orgId}` : 'scale_global';

const getParentOrgUnitId = async (engine, orgUnitId) => {
    if (!orgUnitId) return null;
    try {
        const response = await engine.query({
            orgUnit: {
                resource: `organisationUnits/${orgUnitId}`,
                params: { fields: 'path,parent[id]' }
            }
        });
        const path = response.orgUnit?.path;
        if (path) {
            const pathParts = path.split('/').filter(Boolean);
            if (pathParts.length >= 5) return pathParts[4];
            return response.orgUnit?.parent?.id || null;
        }
        return null;
    } catch (error) {
        return null;
    }
};

// ============================================================
// DATE UTILITY FUNCTIONS (matching ReviewReminders pattern)
// ============================================================

const parseLocalDate = (value) => {
    if (!value) return null;
    
    const dateStr = String(value).split('T')[0];
    const [year, month, day] = dateStr.split('-').map(Number);
    
    if (!year || !month || !day) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) return d;
        return null;
    }
    
    const parsed = new Date(year, month - 1, day);
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
    ) {
        return null;
    }
    return parsed;
};

const dateBelongsToFiscalYearRange = (dateValue, fiscalYear) => {
    const date = parseLocalDate(dateValue);
    const fyStart = parseLocalDate(fiscalYear?.start);
    const fyEnd = parseLocalDate(fiscalYear?.end);

    if (!date || !fyStart || !fyEnd) return false;

    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const normalizedFyStart = new Date(fyStart);
    normalizedFyStart.setHours(0, 0, 0, 0);

    const normalizedFyEnd = new Date(fyEnd);
    normalizedFyEnd.setHours(23, 59, 59, 999);

    return normalizedDate >= normalizedFyStart && normalizedDate <= normalizedFyEnd;
};

const eventBelongsToFiscalYear = (event, fiscalYear) => {
    if (!fiscalYear?.start || !fiscalYear?.end) return true;

    const fyStart = parseLocalDate(fiscalYear.start);
    const fyEnd = parseLocalDate(fiscalYear.end);
    if (!fyStart || !fyEnd) return true;

    // Get date from event
    let eventDate = event.eventDate || event.createdAt || event.occurredAt;
    
    if (eventDate) {
        return dateBelongsToFiscalYearRange(eventDate, fiscalYear);
    }

    // Try to find date in dataValues
    const dataValues = event.dataValues || [];
    const dateFields = ['eventDate', 'reviewDate', 'date', 'createdAt', 'lastUpdated'];
    
    for (const field of dateFields) {
        const found = dataValues.find(dv => dv.dataElement === field)?.value;
        if (found && dateBelongsToFiscalYearRange(found, fiscalYear)) {
            return true;
        }
    }

    return false;
};

// ============================================================
// DATA EXTRACTION FUNCTIONS
// ============================================================

const extractRatingsFromAppraisal = (event) => {
    const dataValues = {};
    event.dataValues?.forEach((dv) => {
        dataValues[dv.dataElement] = dv.value;
    });

    const ratings = [];
    
    // 1. Check for competency ratings (JSON string)
    const compRatingsRaw = dataValues[DATA_ELEMENTS?.EMPLOYEE_COMPETENCY_RATINGS] || 
                           dataValues[DATA_ELEMENTS?.MANAGER_COMPETENCY_RATINGS] || '';
    
    if (compRatingsRaw) {
        try {
            const compRatings = JSON.parse(compRatingsRaw);
            Object.values(compRatings).forEach(rating => {
                const num = parseFloat(rating);
                if (!isNaN(num) && num >= 1 && num <= 5) {
                    ratings.push(num);
                }
            });
        } catch (e) {
            console.warn('Failed to parse competency ratings:', e);
        }
    }

    // 2. Check for self rating task
    const taskRating = dataValues[DATA_ELEMENTS?.EMPLOYEE_SELF_RATING_TASK] || 
                       dataValues['employeeSelfRatingTask'] || '';
    if (taskRating) {
        const num = parseFloat(taskRating);
        if (!isNaN(num) && num >= 1 && num <= 5) {
            ratings.push(num);
        }
    }

    // 3. Check for self rating behavioural
    const behaviouralRating = dataValues[DATA_ELEMENTS?.EMPLOYEE_SELF_RATING_BEHAVIOURAL] || 
                              dataValues['employeeSelfRatingBehavioural'] || '';
    if (behaviouralRating) {
        const num = parseFloat(behaviouralRating);
        if (!isNaN(num) && num >= 1 && num <= 5) {
            ratings.push(num);
        }
    }

    // 4. Check for manager ratings
    const managerTask = dataValues[DATA_ELEMENTS?.MANAGER_RATING_TASK] || 
                        dataValues['managerRatingTask'] || '';
    if (managerTask) {
        const num = parseFloat(managerTask);
        if (!isNaN(num) && num >= 1 && num <= 5) {
            ratings.push(num);
        }
    }

    const managerBehavioural = dataValues[DATA_ELEMENTS?.MANAGER_RATING_BEHAVIOURAL] || 
                               dataValues['managerRatingBehavioural'] || '';
    if (managerBehavioural) {
        const num = parseFloat(managerBehavioural);
        if (!isNaN(num) && num >= 1 && num <= 5) {
            ratings.push(num);
        }
    }

    return ratings;
};

const buildDistribution = (ratings) => {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratings.forEach(rating => {
        const rounded = Math.round(rating);
        if (rounded >= 1 && rounded <= 5) {
            distribution[rounded] += 1;
        }
    });
    return distribution;
};

const calculateStats = (distribution) => {
    const data = Object.entries(distribution).map(([rating, count]) => ({
        rating: parseInt(rating),
        count: count
    }));
    const total = data.reduce((sum, item) => sum + item.count, 0);
    const average = total > 0 
        ? data.reduce((sum, item) => sum + (item.rating * item.count), 0) / total 
        : 0;
    return { data, total, average: average.toFixed(2) };
};

const determinePeriod = (event) => {
    const dataValues = {};
    event.dataValues?.forEach((dv) => {
        dataValues[dv.dataElement] = dv.value;
    });

    const programStage = event.programStage || '';
    
    if (programStage.includes('mid') || programStage.includes('Mid')) {
        return 'Mid-Year';
    }
    if (programStage.includes('end') || programStage.includes('End')) {
        return 'End-Year';
    }
    
    const periodKeys = ['periodType', 'reviewPeriod', 'reviewType', 'period', 'assessmentType'];
    for (const key of periodKeys) {
        if (dataValues[key]) {
            const value = String(dataValues[key]).toLowerCase();
            if (value.includes('mid')) return 'Mid-Year';
            if (value.includes('end')) return 'End-Year';
        }
    }
    
    const dateValue = event.eventDate || event.createdAt || '';
    if (dateValue) {
        const month = new Date(dateValue).getMonth();
        if (month >= 10 || month <= 1) return 'End-Year';
        if (month >= 6 && month <= 9) return 'Mid-Year';
    }
    
    return 'Year-End';
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const LeadershipRatingsDashboard = ({
    performanceData,
    accessibleOrgUnitIds,
    filterScope,
    competencies = [],
    ratingScale: propRatingScale = {},
    orgUnitId,
    positionTEI,
    programStage,
    currentFilter = 'current', // Added: matches ReviewReminders pattern
    selectedFiscalYear = null, // Added: matches ReviewReminders pattern
    fiscalYear = null // Added: current fiscal year from context
}) => {
    const engine = useDataEngine();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ratingScale, setRatingScale] = useState(propRatingScale);
    const [loadingScale, setLoadingScale] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCompetency, setSelectedCompetency] = useState('all');
    const [midYearData, setMidYearData] = useState({ data: [], total: 0, average: 0 });
    const [endYearData, setEndYearData] = useState({ data: [], total: 0, average: 0 });
    const [rawEvents, setRawEvents] = useState({ midYear: [], endYear: [] });

    const [detailsData, setDetailsData] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const isMobile = useMediaQuery('(max-width: 768px)');

    // ============================================================
    // HELPER FUNCTIONS - Defined INSIDE component AFTER state
    // ============================================================

    const getRatingLabel = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].label) {
            return ratingScale[rating].label;
        }
        const labels = {
            1: 'Unsatisfactory',
            2: 'Developing',
            3: 'Competent',
            4: 'Strong Performer',
            5: 'Role Model'
        };
        return labels[rating] || `${rating}`;
    };

    const getRatingColor = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].color) {
            return ratingScale[rating].color;
        }
        const colors = {
            1: '#dc3545',
            2: '#ff9800',
            3: '#ffc107',
            4: '#4caf50',
            5: '#28a745'
        };
        return colors[rating] || '#6c757d';
    };

    const getRatingDescription = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].description) {
            return ratingScale[rating].description;
        }
        const descriptions = {
            1: 'Rarely meets expectations',
            2: 'Sometimes meets expectations',
            3: 'Meets expectations consistently',
            4: 'Frequently exceeds expectations',
            5: 'Consistently exceeds expectations'
        };
        return descriptions[rating] || '';
    };

    // ============================================================
    // FILTER EVENTS BY FISCAL YEAR (matches ReviewReminders pattern)
    // ============================================================

    const filterEventsByFiscalYear = (events) => {
        // If currentFilter is 'all', show all events
        if (currentFilter === 'all') {
            return events;
        }

        // Determine which fiscal year to use
        let fiscalYearToUse = null;
        
        if (selectedFiscalYear?.start && selectedFiscalYear?.end) {
            fiscalYearToUse = selectedFiscalYear;
        } else if (fiscalYear?.start && fiscalYear?.end) {
            fiscalYearToUse = fiscalYear;
        }

        // If no fiscal year, return all events
        if (!fiscalYearToUse) {
            return events;
        }

        // Filter events by fiscal year
        return events.filter(event => 
            eventBelongsToFiscalYear(event, fiscalYearToUse)
        );
    };

    useEffect(() => {
        if (showDetailsModal) {
            setLoadingDetails(true);
            getDetailedData()
                .then(data => {
                    setDetailsData(data);
                    setLoadingDetails(false);
                })
                .catch(err => {
                    console.error('Failed to load details:', err);
                    setDetailsData([]);
                    setLoadingDetails(false);
                });
        }
    }, [showDetailsModal, selectedPeriod, rawEvents, selectedCompetency]);

    // ============================================================
    // FETCH RATING SCALE
    // ============================================================
    useEffect(() => {
        const fetchRatingScale = async () => {
            if (propRatingScale && Object.keys(propRatingScale).length > 0) {
                setRatingScale(propRatingScale);
                return;
            }

            if (!orgUnitId) {
                setRatingScale(getDefaultScale());
                return;
            }

            setLoadingScale(true);
            try {
                const parentOrgId = await getParentOrgUnitId(engine, orgUnitId);
                const orgsToCheck = [orgUnitId];
                if (parentOrgId && parentOrgId !== orgUnitId) {
                    orgsToCheck.push(parentOrgId);
                }

                let loadedScale = null;

                for (const orgId of orgsToCheck) {
                    const key = getDataStoreKey(orgId);
                    try {
                        const response = await engine.query({
                            ratingScale: {
                                resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`,
                            }
                        });

                        const data = response.ratingScale;
                        if (Array.isArray(data) && data.length > 0) {
                            const mapped = {};
                            data.forEach((item) => {
                                const value = Number(item.value);
                                if (!Number.isNaN(value)) {
                                    mapped[value] = {
                                        label: item.label || `${value}`,
                                        color: item.color || getDefaultColor(value),
                                        description: item.description || '',
                                        bgColor: item.bgColor || `${item.color}20`,
                                    };
                                }
                            });

                            if (Object.keys(mapped).length > 0) {
                                loadedScale = mapped;
                                break;
                            }
                        }
                    } catch (error) {
                        console.warn(`No rating scale found for org ${orgId}:`, error);
                    }
                }

                if (!loadedScale) {
                    try {
                        const globalResponse = await engine.query({
                            ratingScale: {
                                resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`,
                            }
                        });
                        const globalData = globalResponse.ratingScale;
                        if (Array.isArray(globalData) && globalData.length > 0) {
                            const mapped = {};
                            globalData.forEach((item) => {
                                const value = Number(item.value);
                                if (!Number.isNaN(value)) {
                                    mapped[value] = {
                                        label: item.label || `${value}`,
                                        color: item.color || getDefaultColor(value),
                                        description: item.description || '',
                                        bgColor: item.bgColor || `${item.color}20`,
                                    };
                                }
                            });
                            if (Object.keys(mapped).length > 0) {
                                loadedScale = mapped;
                            }
                        }
                    } catch (error) {
                        console.warn('No global rating scale found:', error);
                    }
                }

                setRatingScale(loadedScale || getDefaultScale());
            } catch (error) {
                console.error('Failed to fetch rating scale:', error);
                setRatingScale(getDefaultScale());
            } finally {
                setLoadingScale(false);
            }
        };

        fetchRatingScale();
    }, [orgUnitId, engine, propRatingScale]);

    // ============================================================
    // FETCH APPRAISAL EVENTS AND EXTRACT DATA
    // ============================================================
    useEffect(() => {
        const fetchAppraisalData = async () => {
            if (!orgUnitId) {
                console.warn('No orgUnitId provided, skipping data fetch');
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const params = {
                    program: 'yiT00XKrrHP',
                    orgUnit: orgUnitId,
                    ouMode: 'ACCESSIBLE',
                    fields: 'event,programStage,orgUnit,orgUnitName,trackedEntityInstance,eventDate,createdAt,status,dataValues[dataElement,value]',
                    pageSize: 1000,
                };

                if (positionTEI) {
                    params.trackedEntityInstance = positionTEI;
                }

                if (programStage) {
                    params.programStage = programStage;
                }

                console.log('🔍 Fetching appraisal events with params:', params);

                const response = await engine.query({
                    appraisals: {
                        resource: 'events',
                        params: params
                    }
                });

                const allEvents = response.appraisals?.events || [];
                console.log(`📊 Found ${allEvents.length} appraisal events`);

                // ============================================================
                // APPLY FISCAL YEAR FILTERING (matches ReviewReminders)
                // ============================================================
                const filteredEvents = filterEventsByFiscalYear(allEvents);
                console.log(`📊 After fiscal year filter: ${filteredEvents.length} events`);

                // Separate events by period
                const midYearEvents = [];
                const endYearEvents = [];

                filteredEvents.forEach(event => {
                    const period = determinePeriod(event);
                    if (period === 'Mid-Year') {
                        midYearEvents.push(event);
                    } else {
                        endYearEvents.push(event);
                    }
                });

                console.log(`📊 Mid-Year: ${midYearEvents.length}, End-Year: ${endYearEvents.length}`);

                // Process mid-year events
                const midYearRatings = [];
                midYearEvents.forEach(event => {
                    const ratings = extractRatingsFromAppraisal(event);
                    midYearRatings.push(...ratings);
                });

                // Process end-year events
                const endYearRatings = [];
                endYearEvents.forEach(event => {
                    const ratings = extractRatingsFromAppraisal(event);
                    endYearRatings.push(...ratings);
                });

                console.log(`📊 Mid-Year ratings: ${midYearRatings.length}, End-Year ratings: ${endYearRatings.length}`);

                // Build distributions
                const midDistribution = buildDistribution(midYearRatings);
                const endDistribution = buildDistribution(endYearRatings);

                // Calculate stats
                const midStats = calculateStats(midDistribution);
                const endStats = calculateStats(endDistribution);

                setMidYearData(midStats);
                setEndYearData(endStats);
                setRawEvents({
                    midYear: midYearEvents,
                    endYear: endYearEvents
                });

            } catch (err) {
                console.error('Failed to fetch appraisal data:', err);
                setError(err.message || 'Failed to load appraisal data');
            } finally {
                setLoading(false);
            }
        };

        fetchAppraisalData();
    }, [orgUnitId, positionTEI, programStage, engine, currentFilter, selectedFiscalYear, fiscalYear]);

    // ============================================================
    // HANDLER FUNCTIONS
    // ============================================================

    const handleViewDetails = (period, competencyId = null) => {
        setSelectedPeriod(period);
        setSelectedCompetency(competencyId || 'all');
        setShowDetailsModal(true);
    };

    const getDetailedData = async () => {
        const events = selectedPeriod === 'Mid-Year' 
            ? rawEvents.midYear 
            : rawEvents.endYear;

        const filteredEvents = filterEventsByFiscalYear(events);

        // ✅ FIX #1: Deduplicate by TEI (staff member), keep the most recent event
        const uniqueEventsMap = new Map();
        filteredEvents.forEach(event => {
            const teiId = event.trackedEntityInstance || event.event;
            const existing = uniqueEventsMap.get(teiId);
            if (!existing || new Date(event.eventDate) > new Date(existing.eventDate)) {
                uniqueEventsMap.set(teiId, event);
            }
        });
        const uniqueEvents = Array.from(uniqueEventsMap.values());

        // ✅ FIX #2: Fetch all users for mapping (same as LeadershipDashboard)
        let userMap = new Map();
        try {
            let allUsers = [];
            let page = 1;
            let totalPages = 1;
            
            while (page <= totalPages) {
                const userQuery = {
                    users: {
                        resource: 'users',
                        params: {
                            fields: 'id,displayName,username',
                            pageSize: 50,
                            page: page
                        }
                    }
                };
                const result = await engine.query(userQuery);
                const usersData = result.users;
                allUsers = [...allUsers, ...(usersData?.users || [])];
                totalPages = usersData?.pager?.pageCount || 1;
                page++;
            }
            
            userMap = new Map();
            allUsers.forEach(user => {
                userMap.set(user.id, {
                    id: user.id,
                    name: user.displayName,
                    username: user.username
                });
                if (user.username) {
                    userMap.set(user.username, {
                        id: user.id,
                        name: user.displayName,
                        username: user.username
                    });
                }
            });
            console.log(`Loaded ${userMap.size} user mappings`);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }

        // Process each unique event
        const detailedData = [];
        
        for (const [index, event] of uniqueEvents.entries()) {
            const dataValues = {};
            event.dataValues?.forEach((dv) => {
                dataValues[dv.dataElement] = dv.value;
            });

            const staffId = event.trackedEntityInstance;
            let staffName = 'N/A';
            let position = event.orgUnitName || 'N/A';

            // ✅ FIX #3: Multiple strategies to find staff name (same as LeadershipDashboard)
            
            // Strategy 1: Check userMap by staffId (username)
            if (staffId && userMap.has(staffId)) {
                const user = userMap.get(staffId);
                staffName = user.name;
            }

            // Strategy 2: Fetch TEI details to get username or user ID
            if (staffId && staffName === 'N/A') {
                try {
                    const teiQuery = {
                        tei: {
                            resource: `trackedEntityInstances/${staffId}`,
                            params: {
                                fields: 'trackedEntityInstance,attributes[attribute,value],orgUnit'
                            }
                        }
                    };
                    const teiResult = await engine.query(teiQuery);
                    const tei = teiResult.tei;

                    if (tei && tei.attributes) {
                        for (const attr of tei.attributes) {
                            const value = attr.value;
                            
                            // Check if attribute value is a username
                            if (value && userMap.has(value)) {
                                const user = userMap.get(value);
                                staffName = user.name;
                                break;
                            }
                            
                            // Check if value is a user ID
                            if (value && value.length === 11 && /^[A-Za-z0-9]+$/.test(value) && userMap.has(value)) {
                                const user = userMap.get(value);
                                staffName = user.name;
                                break;
                            }
                        }
                    }
                } catch (error) {
                    console.log(`Could not fetch TEI details for ${staffId}:`, error);
                }
            }

            // Strategy 3: Try to get from event data values
            if (staffName === 'N/A') {
                const nameDV = event.dataValues?.find(dv => 
                    dv.dataElement === 'staffName' ||
                    dv.dataElement === 'employeeName' ||
                    dv.dataElement === 'fullName' ||
                    dv.dataElement.includes('name')
                );
                if (nameDV && nameDV.value && nameDV.value !== 'Unknown' && nameDV.value !== 'Staff Member') {
                    staffName = nameDV.value;
                }
            }

            // Strategy 4: Use orgUnitName as last resort (but only if it's not a department)
            if (staffName === 'N/A' && event.orgUnitName && 
                !event.orgUnitName.includes('Department') && 
                !event.orgUnitName.includes('Office')) {
                staffName = event.orgUnitName;
            }

            // ✅ FIX #4: Use DATA_ELEMENTS.STAFF_NAME if it exists in dataValues (highest priority)
            if (dataValues[DATA_ELEMENTS.STAFF_NAME]) {
                staffName = dataValues[DATA_ELEMENTS.STAFF_NAME];
            }

            // Fallback
            if (staffName === 'N/A') {
                staffName = staffId ? `Staff (${staffId.substring(0, 8)})` : 'N/A';
            }

            const ratings = extractRatingsFromAppraisal(event);
            const avgRating = ratings.length > 0 
                ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
                : 'N/A';

            detailedData.push({
                id: event.event || `event-${index}`,
                staffName: staffName,
                position: position,
                reviewer: dataValues[DATA_ELEMENTS?.MANAGER_COMMENTS] || 'N/A',
                rating: avgRating,
                ratingLabel: avgRating !== 'N/A' ? getRatingLabel(Math.round(parseFloat(avgRating))) : 'N/A',
                comments: dataValues[DATA_ELEMENTS?.EMPLOYEE_COMMENTS] || '',
                reviewDate: event.eventDate?.split('T')[0] || 'N/A',
                orgUnit: event.orgUnitName || 'N/A',
                status: dataValues[DATA_ELEMENTS?.APPRAISAL_STATUS] || event.status || 'N/A'
            });
        }

        return detailedData;
    };

    const getImprovementColor = () => {
        const mid = parseFloat(midYearData.average);
        const end = parseFloat(endYearData.average);
        if (mid === 0 && end === 0) return '#ffc107';
        if (end > mid) return '#28a745';
        if (end < mid) return '#dc3545';
        return '#ffc107';
    };

    const getImprovementTrend = () => {
        const mid = parseFloat(midYearData.average);
        const end = parseFloat(endYearData.average);
        if (mid === 0) return end > 0 ? '100' : '0';
        return ((end - mid) / mid * 100).toFixed(1);
    };

    // ============================================================
    // RENDER
    // ============================================================

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <CircularLoader />
                <p style={{ marginTop: '16px' }}>Loading appraisal data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Card style={{ padding: '20px' }}>
                <NoticeBox error title="Error Loading Data">
                    {error}
                </NoticeBox>
            </Card>
        );
    }

    const hasData = midYearData.total > 0 || endYearData.total > 0;

    return (
        <>
            {loadingScale ? (
                <Card style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Loading rating scale configuration...</p>
                </Card>
            ) : (
                <>
                    {!hasData && (
                        <Card style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
                            <p style={{ color: '#666' }}>
                                No appraisal data available for the selected period.
                                {positionTEI && ' No appraisal found for this position.'}
                                {currentFilter === 'all' ? ' (Showing all data)' : ' (Filtered by fiscal year)'}
                            </p>
                        </Card>
                    )}

                    {hasData && (
                        <>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(min(450px, 100%), 1fr))',
                                gap: isMobile ? '16px' : '24px',
                                marginBottom: isMobile ? '16px' : '24px',
                            }}>
                                {midYearData.total > 0 && (
                                    <BellCurveChart
                                        title="Mid-Year Review Performance Distribution"
                                        data={midYearData.data}
                                        period="Mid-Year"
                                        ratingScale={ratingScale}
                                        competencies={competencies}
                                        onViewDetails={() => handleViewDetails('Mid-Year')}
                                        showCompetencyFilter={false}
                                        orgUnitId={orgUnitId}
                                    />
                                )}

                                {endYearData.total > 0 && (
                                    <BellCurveChart
                                        title="End-Year Review Performance Distribution"
                                        data={endYearData.data}
                                        period="End-Year"
                                        ratingScale={ratingScale}
                                        competencies={competencies}
                                        onViewDetails={() => handleViewDetails('End-Year')}
                                        showCompetencyFilter={false}
                                        orgUnitId={orgUnitId}
                                    />
                                )}
                            </div>

                            {midYearData.total > 0 && endYearData.total > 0 && (
                                <Card style={{ marginTop: '8px' }}>
                                    <div style={{ padding: isMobile ? 16 : 20 }}>
                                        <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>
                                            Performance Comparison
                                        </h4>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
                                        gap: '16px'
                                    }}>
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '12px', 
                                            backgroundColor: '#f8f9fa', 
                                            borderRadius: '8px' 
                                        }}>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                Mid-Year Avg Rating
                                            </div>
                                            <div style={{ 
                                                fontSize: '2rem', 
                                                fontWeight: 'bold', 
                                                color: '#28a745' 
                                            }}>
                                                {midYearData.average}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>
                                                ({midYearData.total} reviews)
                                            </div>
                                        </div>

                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '12px', 
                                            backgroundColor: '#f8f9fa', 
                                            borderRadius: '8px' 
                                        }}>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                End-Year Avg Rating
                                            </div>
                                            <div style={{ 
                                                fontSize: '2rem', 
                                                fontWeight: 'bold', 
                                                color: '#28a745' 
                                            }}>
                                                {endYearData.average}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>
                                                ({endYearData.total} reviews)
                                            </div>
                                        </div>

                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '12px', 
                                            backgroundColor: '#f8f9fa', 
                                            borderRadius: '8px' 
                                        }}>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                Improvement Trend
                                            </div>
                                            <div style={{ 
                                                fontSize: '2rem', 
                                                fontWeight: 'bold', 
                                                color: getImprovementColor() 
                                            }}>
                                                {getImprovementTrend()}%
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>
                                                Mid to End-Year
                                            </div>
                                        </div>

                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '12px', 
                                            backgroundColor: '#f8f9fa', 
                                            borderRadius: '8px' 
                                        }}>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                Top Performers (5)
                                            </div>
                                            <div style={{ 
                                                fontSize: '2rem', 
                                                fontWeight: 'bold', 
                                                color: '#28a745' 
                                            }}>
                                                {endYearData.data.find(d => d.rating === 5)?.count || 0}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#999' }}>
                                                End-Year
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                </Card>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Details Modal */}
            {showDetailsModal && (
                <Modal large onClose={() => setShowDetailsModal(false)}>
                    <ModalTitle>
                        {selectedPeriod} Review Details
                        {selectedCompetency !== 'all' && ` - ${selectedCompetency}`}
                    </ModalTitle>
                    <ModalContent>
                        {loadingDetails ? (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <CircularLoader />
                                <p style={{ marginTop: '16px' }}>Loading details...</p>
                            </div>
                        ) : detailsData.length === 0 ? (
                            <p style={{ textAlign: 'center', color: '#666' }}>
                                No detailed data available for {selectedPeriod}.
                            </p>
                        ) : isMobile ? (
                            /* ===== MOBILE: Card layout ===== */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {detailsData.map((item) => (
                                    <div
                                        key={item.id}
                                        style={{
                                            padding: 12,
                                            border: '1px solid #e9ecef',
                                            borderRadius: 8,
                                            backgroundColor: '#fff',
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: 8,
                                            marginBottom: 6,
                                        }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontWeight: 700, fontSize: 14 }}>
                                                    {item.staffName}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                                                    {item.position}
                                                </div>
                                            </div>
                                            {item.rating !== 'N/A' ? (
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 8px',
                                                    borderRadius: 12,
                                                    backgroundColor: getRatingColor(Math.round(parseFloat(item.rating))),
                                                    color: 'white',
                                                    fontSize: 11,
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {item.rating} - {item.ratingLabel}
                                                </span>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: 11 }}>N/A</span>
                                            )}
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: 11,
                                            color: '#6c757d',
                                            marginTop: 6,
                                            paddingTop: 6,
                                            borderTop: '1px solid #f0f0f0',
                                        }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: 12,
                                                backgroundColor: item.status === 'finalized' ? '#d4edda' : '#fff3cd',
                                                color: item.status === 'finalized' ? '#155724' : '#856404',
                                                fontWeight: 'bold',
                                            }}>
                                                {item.status?.toUpperCase() || 'DRAFT'}
                                            </span>
                                            <span>{item.reviewDate}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* ===== DESKTOP: existing table ===== */
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.85rem'
                                }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Staff</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Position</th>
                                            <th style={{ padding: '12px', textAlign: 'center' }}>Avg Rating</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                                            <th style={{ padding: '12px', textAlign: 'left' }}>Review Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailsData.map((item) => (
                                            <tr key={item.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                                                <td style={{ padding: '10px' }}>{item.staffName}</td>
                                                <td style={{ padding: '10px' }}>{item.position}</td>
                                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                                    {item.rating !== 'N/A' ? (
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '4px 8px',
                                                            borderRadius: '12px',
                                                            backgroundColor: getRatingColor(Math.round(parseFloat(item.rating))),
                                                            color: 'white',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {item.rating} - {item.ratingLabel}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#999' }}>N/A</span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '10px' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        backgroundColor: item.status === 'finalized' ? '#d4edda' : '#fff3cd',
                                                        color: item.status === 'finalized' ? '#155724' : '#856404',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {item.status?.toUpperCase() || 'DRAFT'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px' }}>{item.reviewDate}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </ModalContent>
                    <ModalActions>
                        <Button onClick={() => setShowDetailsModal(false)}>Close</Button>
                    </ModalActions>
                </Modal>
            )}
        </>
    );
};

export default LeadershipRatingsDashboard;